import type { Contact } from '@/types/api'
import { emailDomain } from '@/lib/email'

// Mailbox providers: an address on these domains says nothing about the
// contact's company, and its logo would be the provider's.
const GENERIC_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'msn.com',
  'libero.it',
  'virgilio.it',
  'tiscali.it',
  'alice.it',
  'tin.it',
  'tim.it',
  'fastwebnet.it',
  'email.it',
  'inwind.it',
  'iol.it',
  'pec.it',
  'legalmail.it',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'mail.com',
  'yandex.com',
  'web.de',
  'arubapec.it',
  'postecert.it',
  'aruba.it',
  'poste.it',
  'tutanota.com',
  'fastmail.com',
])

// Providers with one domain per country (yahoo.it, hotmail.co.uk, gmx.de...).
const GENERIC_EMAIL_FAMILIES = /^(yahoo|ymail|hotmail|outlook|live|gmx)\.[a-z]{2,}(\.[a-z]{2,})?$/

// Legal forms carry no identity: "Acme S.p.A." reads as "A", not "AS".
const LEGAL_FORMS = new Set(['srl', 'srls', 'spa', 'snc', 'sas', 'inc', 'ltd', 'llc', 'gmbh', 'ag', 'sa', 'corp', 'co'])

// Same rule as the backend proxy (normalize_domain), so no request is spent
// on a domain it would refuse with 422: labels of 1-63 letters, digits and
// inner hyphens, ending in an alphabetic or punycode TLD, 253 chars at most.
const DOMAIN_SHAPE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/
const MAX_DOMAIN_LENGTH = 253

/** Mailbox provider domain, subdomains included (pec.aruba.it, mail.yahoo.co.uk). */
export function isGenericEmailDomain(domain: string): boolean {
  const labels = domain.split('.')
  for (let i = 0; i < labels.length - 1; i++) {
    const suffix = labels.slice(i).join('.')
    if (GENERIC_EMAIL_DOMAINS.has(suffix) || GENERIC_EMAIL_FAMILIES.test(suffix)) return true
  }
  return false
}

/** Bare hostname out of a domain typed by hand, possibly as a URL ("https://www.acme.com/"). */
function cleanDomain(value: string | null | undefined): string | null {
  const domain = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split(/[/?#:]/)[0]
    .replace(/^www\./, '')
    .replace(/\.$/, '')
  return domain.length <= MAX_DOMAIN_LENGTH && DOMAIN_SHAPE.test(domain) ? domain : null
}

/**
 * Domain to derive the company logo from: the associated company's domain,
 * otherwise the domain of the contact email; mailbox providers never count.
 */
export function companyLogoDomain(contact: Pick<Contact, 'email' | 'properties'>): string | null {
  const candidates = [cleanDomain(contact.properties.company_domain), cleanDomain(emailDomain(contact.email))]
  return candidates.find((domain) => domain !== null && !isGenericEmailDomain(domain)) ?? null
}

// Edge characters ignored in a company name: the same set the backend strips
// (BLANK_CHARS in backend/app/services/contact_company.py), not String.trim().
const BLANK_EDGES = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g

function cleanCompanyName(value: unknown): string {
  return value == null ? '' : String(value).replace(BLANK_EDGES, '')
}

/**
 * Company shown for a contact: the one associated on HubSpot (the same the logo
 * comes from), else the contact's own text field, which HubSpot leaves stale
 * when the person changes company (#331). Blank values count as missing.
 * Same rule as the backend's `displayed_company` / `DISPLAYED_COMPANY`, which
 * drives sort, search, company filter and the `{{company}}` email variable.
 */
export function contactCompanyName(contact: Pick<Contact, 'company' | 'properties'>): string | null {
  return cleanCompanyName(contact.properties?.company_name) || cleanCompanyName(contact.company) || null
}

const ALPHANUMERIC = /[\p{L}\p{N}]/u

/**
 * Up to two initials of a company name, skipping legal forms, as the logo
 * fallback. A name made only of legal forms and symbols ("S.p.A.") still gets
 * its first letter or digit.
 */
export function companyInitials(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter((word) => !LEGAL_FORMS.has(word.toLowerCase().replace(/\./g, '')))
    .map((word) => word.match(ALPHANUMERIC)?.[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
  return (initials || name.match(ALPHANUMERIC)?.[0] || '').toUpperCase()
}
